import { useTranslation } from 'react-i18next';
import type { AgenticRecommendation } from '@/features/agentic-insights/components/data/types';
import { RunProse } from '@/features/agentic-insights/components/experimental/shared/prose/RunProse';
import { MetaLine } from '@/features/agentic-insights/components/experimental/shared/ui/controls';
import { Field } from '@/features/agentic-insights/components/experimental/shared/ui/Field';
import { Tooltip } from '@/features/agentic-insights/components/experimental/shared/ui/Tooltip';
import { TYPE } from '../language';

/**
 * An action's two parameters — what it applies to, and the horizon it wants doing in — as one meta
 * line.
 *
 * Values only, each carrying its field name as a hover tooltip rather than beside it: "APPLIES TO"
 * is longer than half the objective names it introduces, and the values are self-describing anyway.
 * The name is still the accessible description, so nothing is lost without a pointer.
 *
 * `target`'s objective links to the objectives screen — the same offer a finding's scope makes. It
 * links only when the title resolved: a value reading "Objective 68" has nothing to open.
 *
 * Shared by the work order and the finding expansion because it is the same two fields in both. They
 * had drifted into labelled-inline in one place and values-with-tooltips in the other, so the same
 * objective was a link on one screen and plain text on the other.
 *
 * `target` is "Owner" in the classic tab, but it holds a thing ("[objective:68] and [item:14743]"),
 * so it names what the action applies to and never a person. `deadline` holds a date OR a planning
 * horizon, so "Time horizon" covers both without promising precision the value does not have.
 */
export const ActionParameters = ({ recommendation }: { recommendation: AgenticRecommendation }) => {
  const { t } = useTranslation();

  const value = (key: 'target' | 'deadline', label: string, text: string) => (
    <span key={key} className="font-medium text-text-neutral-primary normal-case">
      <Tooltip label={label}>
        <Field name={`recommendations[].${key}`}>
          <RunProse references="link">{text}</RunProse>
        </Field>
      </Tooltip>
    </span>
  );

  const items = [
    recommendation.target
      ? value(
          'target',
          t(($) => $.loopAgenticInsights.experimental.recommendation.appliesTo),
          recommendation.target,
        )
      : null,
    recommendation.deadline
      ? value(
          'deadline',
          t(($) => $.loopAgenticInsights.experimental.recommendation.timeHorizon),
          recommendation.deadline,
        )
      : null,
  ];

  return <MetaLine type={TYPE} items={items} />;
};
